import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { DEFAULT_TABLE_PAGE_SIZE } from 'src/constants/Pagination';
import AvailableFilterOptionsService from 'src/services/placementOverview/availableFilterOptionsService';
import FilterTagsAndOptionsService from 'src/services/placementOverview/filterTagsAndOptionsService';
import PlacementFilterService from 'src/services/placementOverview/placementFilterService';
import PlacementOverviewService from 'src/services/placementOverview/placementOverviewService';
import PlacementTableDataService from 'src/services/placementOverview/placementTableDataService';
import { type AvailableFilterOptions } from 'src/services/placementOverview/types/availableFilterOptions';
import { type FilterOptions } from 'src/services/placementOverview/types/filterOptions';
import { type FilterTag } from 'src/services/placementOverview/types/filterTag';
import { type PlacementData } from 'src/services/placementOverview/types/placementData';
import { type TableDataRow } from 'src/services/placementOverview/types/tableDataRow';
import { type RootState } from 'src/store/store';
import { ApiCallStatus } from 'src/store/type';

export type GlobalFilterOptions = {
  placementType: string | null;
};

export type PlacementOverviewPageState = {
  placementsStatus: ApiCallStatus;
  tableData: TableDataRow[];
  allPlacementsTableData: TableDataRow[];
  activePlacementsTableData: TableDataRow[];
  expiredPlacementsTableData: TableDataRow[];
  pendingPlacementsTableData: TableDataRow[];
  closedPlacementsTableData: TableDataRow[];
  transferredInPlacementsTableData: TableDataRow[];
  currentPage: number;
  totalPages: number;
  searchField: string;
  isUserDaliUser: boolean;
  isAdvancedFilterSheetOpen: boolean;
  availableFilterOptions: AvailableFilterOptions;
  selectedFilterOptions: FilterOptions;
  appliedFilterOptions: FilterOptions;
  globalFilterOptions: GlobalFilterOptions;
  filterTags: FilterTag[];
  perPage: number;
};

const initialFilterOptions: FilterOptions = {
  sites: [],
  jobs: [],
  daysInCategory: [],
  jobTypes: [],
  shiftCodes: [],
  placementStartDate: '',
  placementEndDate: '',
};

const initialGlobalFilterOptions: GlobalFilterOptions = {
  placementType: null,
};

const initialAvailableFilterOptions: AvailableFilterOptions = {
  availableSites: [],
  availablePlacementTypes: [],
  availableJobs: [],
  availableDaysInCategory: [],
  availableJobTypes: [],
  availableShiftCodes: [],
};

const initialState: PlacementOverviewPageState = {
  placementsStatus: ApiCallStatus.Idle,
  tableData: [],
  allPlacementsTableData: [],
  activePlacementsTableData: [],
  expiredPlacementsTableData: [],
  pendingPlacementsTableData: [],
  closedPlacementsTableData: [],
  transferredInPlacementsTableData: [],
  currentPage: 1,
  totalPages: 0,
  searchField: '',
  isUserDaliUser: false,
  isAdvancedFilterSheetOpen: false,
  availableFilterOptions: initialAvailableFilterOptions,
  selectedFilterOptions: initialFilterOptions,
  appliedFilterOptions: initialFilterOptions,
  globalFilterOptions: initialGlobalFilterOptions,
  filterTags: [],
  perPage: DEFAULT_TABLE_PAGE_SIZE,
};

/**
 * get open placements by requester login
 * @returns {Object}
 */
export const getOpenPlacementsByRequesterLogin = createAsyncThunk(
  'placements/getByRequesterLogin',
  async (requesterLogin: string, { getState }) => {
    const state = await (getState() as any);
    const { data } = await PlacementOverviewService.getOpenPlacementsByRequesterLogin(requesterLogin);
    const appliedFilterOptions = state.placementOverviewPage.appliedFilterOptions;
    return { data, appliedFilterOptions };
  },
);

/**
 * get open and closed placements by site id
 * @returns {Object}
 */
export const getOpenAndClosedPlacementsBySiteId = createAsyncThunk(
  'placements/getBySiteId',
  async (siteId: number, { getState }) => {
    const state = await (getState() as any);
    const { data: openPlacements } = await PlacementOverviewService.getOpenPlacementsBySiteId(siteId);
    const { data: closedPlacements } = await PlacementOverviewService.getClosedPlacementsBySiteId(siteId);
    const appliedFilterOptions = state.placementOverviewPage.appliedFilterOptions;
    const placements = openPlacements.concat(closedPlacements);
    return { placements, appliedFilterOptions };
  },
);

/** Placement Overview Page Slice */
const { reducer, actions } = createSlice({
  name: 'placementOverviewPageSlice',
  initialState,
  reducers: {
    setIsDaliUser: (state, action) => {
      state.isUserDaliUser = action.payload;
    },
    setSearchField: (state, action) => {
      state.searchField = action.payload;
    },
    setIsAdvancedFilterSheetOpen: (state, action) => {
      state.isAdvancedFilterSheetOpen = action.payload;
    },
    setSelectedFilterOptions: (state, action) => {
      state.selectedFilterOptions = action.payload;
    },
    setGlobalFilterOptions: (state, action) => {
      state.globalFilterOptions = { ...state.globalFilterOptions, ...action.payload };
      updateFilteredPlacementData(state);
      state.currentPage = 1;
    },
    resetFilters: (state) => {
      state.selectedFilterOptions = initialFilterOptions;
    },
    applyFilters: (state) => {
      state.appliedFilterOptions = state.selectedFilterOptions;
      updateFilteredPlacementData(state);
      state.filterTags = FilterTagsAndOptionsService.createFilterTags(state.appliedFilterOptions);
      state.currentPage = 1;
    },
    setAppliedFilterOptions: (state, action) => {
      state.appliedFilterOptions = action.payload;
    },
    removeTagAndUpdateFilters: (state, action) => {
      state.filterTags = FilterTagsAndOptionsService.removeFilterTag(state.filterTags, action.payload);
      state.appliedFilterOptions = FilterTagsAndOptionsService.getUpdatedAppliedFilterOptionsOnTagRemoval(
        state.appliedFilterOptions,
        action.payload.optionName,
        action.payload.value,
      );
      state.selectedFilterOptions = state.appliedFilterOptions;
      updateFilteredPlacementData(state);
      state.currentPage = 1;
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    setTotalPages: (state, action) => {
      state.totalPages = action.payload;
    },
    setPerPage: (state, action) => {
      state.perPage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getOpenPlacementsByRequesterLogin.pending, (state) => {
      state.placementsStatus = ApiCallStatus.Pending;
    });
    builder.addCase(getOpenPlacementsByRequesterLogin.fulfilled, (state, { payload }) => {
      state.tableData = payload.data.map((placementData: PlacementData) =>
        PlacementTableDataService.placementsDataToTableRow(placementData),
      );
      state.availableFilterOptions = AvailableFilterOptionsService.extractAvailableFilterOptions(state.tableData);
      state.globalFilterOptions = reconcileGlobalFilterOptions(
        state.globalFilterOptions,
        state.availableFilterOptions.availablePlacementTypes,
      );
      updateFilteredPlacementData(state);
      state.placementsStatus = ApiCallStatus.Success;
    });
    builder.addCase(getOpenPlacementsByRequesterLogin.rejected, (state) => {
      state.placementsStatus = ApiCallStatus.Error;
    });

    builder.addCase(getOpenAndClosedPlacementsBySiteId.pending, (state) => {
      state.placementsStatus = ApiCallStatus.Pending;
    });
    builder.addCase(getOpenAndClosedPlacementsBySiteId.fulfilled, (state, { payload }) => {
      state.tableData = payload.placements.map((placementData: PlacementData) =>
        PlacementTableDataService.placementsDataToTableRow(placementData),
      );
      state.availableFilterOptions = AvailableFilterOptionsService.extractAvailableFilterOptions(state.tableData);
      state.globalFilterOptions = reconcileGlobalFilterOptions(
        state.globalFilterOptions,
        state.availableFilterOptions.availablePlacementTypes,
      );
      updateFilteredPlacementData(state);
      state.placementsStatus = ApiCallStatus.Success;
    });
    builder.addCase(getOpenAndClosedPlacementsBySiteId.rejected, (state) => {
      state.placementsStatus = ApiCallStatus.Error;
    });
  },
});

const reconcileGlobalFilterOptions = (
  globalFilterOptions: GlobalFilterOptions,
  availablePlacementTypes: string[],
): GlobalFilterOptions => {
  if (
    globalFilterOptions.placementType !== null &&
    !availablePlacementTypes.includes(globalFilterOptions.placementType)
  ) {
    return { ...globalFilterOptions, placementType: null };
  }
  return globalFilterOptions;
};

const updateFilteredPlacementData = (state: PlacementOverviewPageState) => {
  const {
    allPlacementData,
    pendingPlacementData,
    activePlacementData,
    expiredPlacementData,
    closedPlacementData,
    transferredInPlacementData,
  } = extractAndFilter(state.tableData, state.appliedFilterOptions, state.globalFilterOptions);
  state.allPlacementsTableData = allPlacementData;
  state.pendingPlacementsTableData = pendingPlacementData;
  state.activePlacementsTableData = activePlacementData;
  state.expiredPlacementsTableData = expiredPlacementData;
  state.closedPlacementsTableData = closedPlacementData;
  state.transferredInPlacementsTableData = transferredInPlacementData;
};

const extractAndFilter = (
  tableData: TableDataRow[],
  appliedFilterOptions: FilterOptions,
  globalFilterOptions: GlobalFilterOptions,
) => {
  const placementTypes = globalFilterOptions.placementType === null ? [] : [globalFilterOptions.placementType];
  const sourceFilteredTableData = PlacementFilterService.filterPlacementsByPlacementTypes(tableData, placementTypes);
  const pendingPlacementData = PlacementTableDataService.extractAndFilterPending(
    sourceFilteredTableData,
    appliedFilterOptions,
  );
  const activePlacementData = PlacementTableDataService.extractAndFilterActive(
    sourceFilteredTableData,
    appliedFilterOptions,
  );
  const expiredPlacementData = PlacementTableDataService.extractAndFilterExpired(
    sourceFilteredTableData,
    appliedFilterOptions,
  );
  const closedPlacementData = PlacementTableDataService.extractAndFilterClosed(
    sourceFilteredTableData,
    appliedFilterOptions,
  );
  const transferredInPlacementData = PlacementFilterService.filterPlacements(
    PlacementTableDataService.extractTransferred(sourceFilteredTableData),
    appliedFilterOptions,
  );
  // Status categories are disjoint, so All remains the sum of the filtered tab datasets.
  const allPlacementData = [
    ...pendingPlacementData,
    ...activePlacementData,
    ...expiredPlacementData,
    ...closedPlacementData,
    ...transferredInPlacementData,
  ];

  return {
    allPlacementData,
    pendingPlacementData,
    activePlacementData,
    expiredPlacementData,
    closedPlacementData,
    transferredInPlacementData,
  };
};

export const selectHasTransferredInPlacements = (state: RootState) =>
  state.placementOverviewPage.tableData.some(({ category }) => category === 'Transferred In');

export const selectVisibleAllPlacementsTableData = createSelector(
  [
    (state: RootState) => state.placementOverviewPage.allPlacementsTableData,
    (state: RootState) => state.user.featureFlags.PLACEMENT_TRANSFER,
  ],
  (allPlacementsTableData, isPlacementTransferEnabled) =>
    isPlacementTransferEnabled
      ? allPlacementsTableData
      : allPlacementsTableData.filter(({ category }) => category !== 'Transferred In'),
);

export const {
  setIsDaliUser,
  setSearchField,
  setIsAdvancedFilterSheetOpen,
  setAppliedFilterOptions,
  setSelectedFilterOptions,
  setGlobalFilterOptions,
  resetFilters,
  applyFilters,
  removeTagAndUpdateFilters,
  setCurrentPage,
  setTotalPages,
  setPerPage,
} = actions;

export default reducer;
